/* eslint-disable no-console */
import { mapGetters, mapActions, mapState } from 'vuex';
import contentHeader from '@/components/generics/content-header/content-header.vue';
import formCheckbox from '@/components/forms/checkbox/checkbox.vue';
import profile from '@/components/account/profile/profile.vue';
import switchButton from '@/components/generics/switch-button/switch-button.vue';
import alertBox from '@/components/generics/alert-box/alert-box.vue';
import loaderMixin from '@/mixins/loader';
import externalLinksMixin from '@/mixins/externalLinks';
import constants from '@/constants';
import { MODALS } from '@/components/app/app-modals/app-modals';
import settingsOption from './settings-option/settings-option.vue';
import checkOption from './check-option/check-option.vue';

const PROVIDER_FACEBOOK = 'Facebook';
const PROVIDER_TWITTER = 'Twitter';

const optionPush = {
  key: 'AllowPushNotifications',
  info: 'Push Notifications',
};

const sectionNotifications = [
  {
    key: 'OpenOfferMatched',
    info: 'One of my offers has been matched',
  },
  {
    key: 'MarketClosed',
    info: 'One of my markets has closed',
  },
];

if (process.env.SHOW_PRICE_NOTIFICATION_SETTINGS) {
  sectionNotifications.push({
    key: 'ContractPriceChange',
    info: 'One of my contracts changes in price by:',
    withValue: 'ContractPriceChangeAmount',
    validate: x => x >= 5 && x < 100,
    currencyInitial: 5,
  });
}

const sectionGeneral = [
  // {
  //   key: 'ShowBalanceHeader',
  //   info: 'Show my available balances in header',
  // },
];


export default {
  components: {
    alertBox,
    contentHeader,
    formCheckbox,
    profile,
    switchButton,
    settingsOption,
    checkOption,
  },
  mixins: [
    externalLinksMixin,
    loaderMixin,
  ],
  data() {
    return {
      manageInfoLoaded: false,
      correspondence: false,
      logins: [],
      hasLocalPassword: false,
      sectionGeneral,
      sectionNotifications,
      optionPush,
      sectionPushEnabled: false,
      categories: {
        privacy: 'privacy',
        general: 'display',
        push: 'PushNotifications',
        inApp: 'InAppNotifications',
      },
    };
  },
  metaInfo() {
    return {
      title: 'Settings',
    };
  },
  computed: {
    ...mapGetters('app', ['isLayoutMobile']),
    ...mapGetters('profile', ['settingsReady']),
    ...mapState('profile', ['settings']),
    sectionPrivacy() {
      const results = [
        {
          key: 'ShowTradeHistory',
          info: "Show my trade activity in 'Currently Predicting'",
        },
      ];
      if (process.env.LEADERBOARDS_ENABLED) {
        results.push(  {
          key: 'AllowLeaderboard',
          info: 'Show my ranking in the Leaderboards',
        });
      }
      return results;
    },
    gridRowModifier() {
      return {
        row: this.isLayoutMobile,
        'row-x': !this.isLayoutMobile,
      };
    },
    isFacebookDisabled() {
      return !this.hasLocalPassword;
    },
    isTwitterDisabled() {
      return !this.hasLocalPassword;
    },
    hasFacebook() {
      return this.checkLogin(PROVIDER_FACEBOOK);
    },
    hasTwitter() {
      return this.checkLogin(PROVIDER_TWITTER);
    },
    hasGeneral() {
      if (this.settings.display && typeof this.settings.display.ShowBalanceHeader !== 'undefined') {
        this.loaderRemove(document.body);
        return true;
      }
      return false;
    },
    hasPrivacy() {
      if (
        this.settings.privacy
        && typeof this.settings.privacy.ShowTradeHistory !== 'undefined'
        && typeof this.settings.privacy.AllowLeaderboard !== 'undefined'
      ) {
        this.loaderRemove(document.body);
        return true;
      }
      return false;
    },
    hasPush() {
      if (this.settings.PushNotifications && typeof this.settings.PushNotifications.AllowPushNotifications !== 'undefined') {
        this.sectionPushEnabled = this.settings.PushNotifications.AllowPushNotifications;
        this.loaderRemove(document.body);
        return true;
      }
      return false;
    },
    isPushDisabled() {
      return !this.sectionPushEnabled;
    },
  },
  created() {
    this.loaderAdd(document.body);
    this.getAllSettings()
      .then(this.checkManageInfo)
      .then(() => {
        this.manageInfoLoaded = true;
      })
      .then(() => this.getExternalLogins({
        type: constants.SOCIAL_LOGIN_ADD,
      }))
      .then((response) => {
        this.links = response.data;
      });
  },
  methods: {
    ...mapActions('notifications', ['setErrorSideNotification', 'setSuccessSideNotification']),
    ...mapActions('user', ['getManageInfo', 'removeExternal', 'logout']),
    ...mapActions('popup', ['setCurrentSmallPopup']),
    ...mapActions('profile', ['getAllSettings']),
    checkProvider(x, target) {
      return x.loginProvider.toLowerCase() === target.toLowerCase();
    },
    checkLogin(target) {
      return this.logins &&
        this.logins.length > 0 &&
        (this.logins.filter(x => this.checkProvider(x, target)).length === 1);
    },
    getProviderKey(target) {
      if (this.logins && this.logins.length > 0) {
        const login = this.logins.filter(x => this.checkProvider(x, target))[0];
        return (login) ? login.providerKey : {};
      }
      return {};
    },
    checkManageInfo() {
      return this.getManageInfo()
        .then((data) => {
          this.hasLocalPassword = data.hasLocalPassword;
          this.logins = data.logins;
        });
    },
    onToggleProvider(provider, value) {
      if (value) {
        this.loaderAdd(document.body);
        if (provider === PROVIDER_FACEBOOK) this.facebookLinkClick();
        if (provider === PROVIDER_TWITTER) this.twitterLinkClick();
      } else {
        const payload = {
          loginProvider: provider,
          providerKey: this.getProviderKey(provider),
        };
        this.removeExternal(payload)
          .then(() => this.setSuccessSideNotification('Settings updated successfully.'))
          .then(this.getAllSettings)
          .then(this.checkManageInfo)
          .catch(() => this.setErrorSideNotification('Error has occured.'));
      }
    },
    onToggleSetting(item) {
      if (item.key === 'AllowLeaderboard') {
        this.setCurrentSmallPopup(MODALS.SMALL_LEADERBOARDS_UPDATE_INFO);
      }
    },
    onSetPassword() {
      this.$router.push({ name: 'password-set' });
    },
    doError(e) {
      this.setErrorSideNotification(e);
    },
    togglePush(e) {
      this.sectionPushEnabled = e;
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/account/settings/settings.js